import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { Routes, RouterModule } from '@angular/router';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { NgxUiLoaderModule, NgxUiLoaderConfig, SPINNER } from 'ngx-ui-loader';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { GridModule } from '@progress/kendo-angular-grid';
import { InputsModule } from '@progress/kendo-angular-inputs';

import { AppComponent } from './app.component';
import { InvestorHomeComponent } from './all-home-pages/investor-home/investor-home.component';
import { LoginPageComponent } from './login-page/login-page.component';
import { Login2FaComponent } from './login-2fa/login-2fa.component';
import { FormsComponent } from './forms/forms.component';
import { ReportsComponent } from './reports/reports.component';
import { TopMenuComponent } from './top-menu/top-menu.component';
import { LeftMenuComponent } from './left-menu/left-menu.component';
import { LookingForHelpComponent } from './looking-for-help/looking-for-help.component';
import { AbstractDataProvider } from './AbstractDataProvider';
import { LoginProvider } from 'src/shared/service/login.service';
import { JqueryDialogService } from './ui-components/jquery-dialog/jquery-dialog.service';
import { AuthServiceCustom } from 'src/shared/service/auth.service';
import { TokenInterceptor } from './security/interceptors/token.interceptor';
import { TaxComponent } from './tax/tax.component';
import { StatementComponent } from './statement/statement.component';

import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';

import { InvestmentsComponent } from './investments/investments.component';
import { InboxComponent } from './inbox/inbox.component';
import { PopUpDialogComponent } from './pop-up-dialog/pop-up-dialog.component';
import { EnPageNameEnum } from './security/page-name.enum';
import { SecurityQuestionComponent } from './security-question/security-question.component';
import { ForgotPasswordComponent } from './forgot-password/forgot-password.component';
import { ChangePasswordComponent } from './change-password/change-password.component';
import { UserAuthorizedCanActivate } from './security/user-authorized.guard.';
import { InvestorRegistrationComponent } from './new-registration/investor-registration/investor-registration.component';
import { FAQComponent } from './faq/faq.component';
import { ProfileComponent } from './profile/profile.component';
import { FormatLabelComponent } from './ui-components/format-label.component';
import { ClientSearchComponent } from './client-search/client-search.component';
import { ResetPasswordComponent } from './reset-password/reset-password.component';
import { DynamicModalService } from './ui-components/dynamic-modals/dynamic-modal.service';
import { DynamicModalComponent } from './ui-components/dynamic-modals/dynamic-modals.component';
import { ModalContainerDirective } from './ui-components/dynamic-modals/modal-container.directive';
import { UpdateAddressComponent } from './update-address/update-address.component';
import { StarMaskedTextBoxComponent } from './ui-components/star-masked-textbox.component';
import { DateChooserComponent } from './ui-components/date-chooser.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { LocationStrategy, HashLocationStrategy } from '@angular/common';

import { EditProfileComponent } from './edit-profile/edit-profile.component';
import { CheckboxComponent } from './ui-components/checkbox/checkbox.component';
import { UiComponentService } from './ui-components/ui-component.service';
import { GenericHomePageComponent } from './generic-home-page/generic-home-page.component';
import { RepHomeComponent } from './all-home-pages/rep-home/rep-home.component';
import { BrokerHomeComponent } from './all-home-pages/broker-home/broker-home.component';
import { CustodianHomeComponent } from './all-home-pages/custodian-home/custodian-home.component';
import { UserHomeComponent } from './all-home-pages/user-home/user-home.component';
import { OtherUserHomeComponent } from './all-home-pages/other-user-home/other-user-home.component';
import { InvestmentDetailComponent } from './investments/investment-detail/investment-detail.component';
import { ClientStatementComponent } from './client-statements/client-statement.component';
import { ContactsComponent } from './contacts/contacts.component';
import { FormsBlockComponent } from './all-home-pages/helper-components/forms-block/forms-block.component';
import { AnnouncementBlockComponent } from './all-home-pages/helper-components/announcement-block/announcement-block.component';
import { TableTitleComponent } from './table-title/table-title.component';
import { DocumentsBlockComponent } from './all-home-pages/helper-components/documents-block/documents-block.component';
import { PortfolioBlockComponent } from './all-home-pages/helper-components/portfolio-block/portfolio-block.component';
import { DictionaryService } from '../shared/service/dictionary.service';
import { DictionaryPipe } from '../shared/pipes/dictionary.pipe';
import { InvestmentsBlockComponent } from './all-home-pages/helper-components/investments-block/investments-block.component';
import { StepperComponent } from './new-registration/stepper/stepper.component';
import { SecureMaskComponent } from './secure-mask/secure-mask.component';
import { PopUpConfirmationComponent } from './pop-up-confirmation/pop-up-confirmation.component';
import { RepRegistrationComponent } from './new-registration/rep-registration/rep-registration.component';
import { LinkAccountModalComponent } from './link-account-modal/link-account-modal.component';
import { ForgotUsernameComponent } from './forgot-username/forgot-username.component';
import { UserUnAuthorizedCanActivate } from './security/user-unauthorized.guard';
import { SecurityAnswersComponent } from './reset-password/security-answers/security-answers.component';
import { EditSecurityQuestionsComponent } from './edit-profile/edit-security-questions/edit-security-questions.component';
import { DeactivateUserPopupComponent } from './deactivate-user-popup/deactivate-user-popup.component';
import { ActivateUserPopupComponent } from './activate-user-popup/activate-user-popup.component';
import {AddNewLoginModalComponent} from './add-new-login-modal/add-new-login-modal.component';
import {ProxyComponent} from './proxy/proxy.component';
import {NotProxyOnlyGuard} from './security/not-proxy-guard.service';
import {LabelModule} from '@progress/kendo-angular-label';
import { AuthModule } from '@auth0/auth0-angular';
import { environment } from 'src/environments/environment';


const ngxUiLoaderConfig: NgxUiLoaderConfig = {
  bgsType: SPINNER.ballSpinClockwise,
  fgsType: SPINNER.ballSpinClockwise
};

const redirectUri = (window.location.origin + window.location.pathname).replace(/\/?$/, '/').toLowerCase();

const parentroutes: Routes = [
  // {
  //   path: '',
  //   redirectTo: 'index',
  //   pathMatch: 'full',
  // },
  {path: '', component: LoginPageComponent, pathMatch: 'full'},
  {
    path: 'index',
    component: LoginPageComponent,
    runGuardsAndResolvers: 'always',
    canActivate: [UserUnAuthorizedCanActivate, NotProxyOnlyGuard],
  }, // Login Page
  {
    path: 'login2fa',
    component: Login2FaComponent,
    data: {Title: 'Authenticate Your Account'},
    runGuardsAndResolvers: 'always',
    canActivate: [NotProxyOnlyGuard],
  },
  {
    path: 'home',
    component: GenericHomePageComponent,
    data: {screenId: EnPageNameEnum.Home},
    runGuardsAndResolvers: 'always',
    canActivate: [UserAuthorizedCanActivate, NotProxyOnlyGuard]
  },
  {
    path: 'investorhome',
    component: InvestorHomeComponent,
    data: {screenId: EnPageNameEnum.Home},
    runGuardsAndResolvers: 'always',
    canActivate: [UserAuthorizedCanActivate, NotProxyOnlyGuard]
  },
  {
    path: 'rephome',
    component: RepHomeComponent,
    data: {screenId: EnPageNameEnum.Home},
    runGuardsAndResolvers: 'always',
    canActivate: [UserAuthorizedCanActivate, NotProxyOnlyGuard]
  },
  {
    path: 'brokerhome',
    component: BrokerHomeComponent,
    data: {screenId: EnPageNameEnum.Home},
    runGuardsAndResolvers: 'always',
    canActivate: [UserAuthorizedCanActivate, NotProxyOnlyGuard]
  },
  {
    path: 'custodianhome',
    component: CustodianHomeComponent,
    data: {screenId: EnPageNameEnum.Home}, runGuardsAndResolvers: 'always',
    canActivate: [UserAuthorizedCanActivate, NotProxyOnlyGuard]
  },
  {
    path: 'userhome',
    component: UserHomeComponent,
    data: {screenId: EnPageNameEnum.Home},
    runGuardsAndResolvers: 'always',
    canActivate: [UserAuthorizedCanActivate, NotProxyOnlyGuard]
  },
  {
    path: 'otheruserhome',
    component: OtherUserHomeComponent,
    data: {screenId: EnPageNameEnum.Home},
    runGuardsAndResolvers: 'always',
    canActivate: [UserAuthorizedCanActivate, NotProxyOnlyGuard]
  },
  {
    path: 'reports',
    component: ReportsComponent,
    data: {screenId: EnPageNameEnum.Reports},
    runGuardsAndResolvers: 'always',
    canActivate: [UserAuthorizedCanActivate, NotProxyOnlyGuard]
  },
  {
    path: 'forms',
    component: FormsComponent,
    data: {screenId: EnPageNameEnum.Forms},
    runGuardsAndResolvers: 'always',
    canActivate: [UserAuthorizedCanActivate, NotProxyOnlyGuard]
  },
  {
    path: 'tax',
    component: TaxComponent,
    data: {screenId: EnPageNameEnum.Taxes},
    runGuardsAndResolvers: 'always',
    canActivate: [UserAuthorizedCanActivate, NotProxyOnlyGuard]
  },
  {
    path: 'statement',
    component: StatementComponent,
    data: {screenId: EnPageNameEnum.Statements},
    runGuardsAndResolvers: 'always',
    canActivate: [UserAuthorizedCanActivate, NotProxyOnlyGuard]
  },
  {
    path: 'repStatement',
    component: StatementComponent,
    data: {screenId: EnPageNameEnum.RepStatements},
    runGuardsAndResolvers: 'always',
    canActivate: [UserAuthorizedCanActivate, NotProxyOnlyGuard]
  },
  {
    path: 'clientStatement',
    component: ClientStatementComponent,
    data: {screenId: EnPageNameEnum.ClientStatements}, runGuardsAndResolvers: 'always',
    canActivate: [UserAuthorizedCanActivate, NotProxyOnlyGuard]
  },
  {
    path: 'investment',
    component: InvestmentsComponent,
    data: {screenId: EnPageNameEnum.Investments}, runGuardsAndResolvers: 'always',
    canActivate: [UserAuthorizedCanActivate, NotProxyOnlyGuard]
  },
  {
    path: 'proxy',
    component: ProxyComponent,
    data: {screenId: EnPageNameEnum.Proxy}, runGuardsAndResolvers: 'always',
  },
  {
    path: 'inbox',
    component: InboxComponent,
    data: {screenId: EnPageNameEnum.Inbox},
    runGuardsAndResolvers: 'always',
    canActivate: [UserAuthorizedCanActivate, NotProxyOnlyGuard]
  },
  {
    path: 'announcements',
    component: InboxComponent,
    data: {screenId: EnPageNameEnum.Announcements}, runGuardsAndResolvers: 'always',
    canActivate: [UserAuthorizedCanActivate, NotProxyOnlyGuard]
  },
  {
    path: 'changepassword',
    component: ChangePasswordComponent,
    data: {screenId: EnPageNameEnum.ChangePassword, Title: 'Change Password'}, runGuardsAndResolvers: 'always',
    canActivate: [UserAuthorizedCanActivate, NotProxyOnlyGuard]
  },
  {
    path: 'changeaddress',
    component: UpdateAddressComponent,
    data: {screenId: EnPageNameEnum.UpdateAddress, Title: 'Change Address'}, runGuardsAndResolvers: 'always',
    canActivate: [UserAuthorizedCanActivate, NotProxyOnlyGuard]
  },
  {
    path: 'my-profile',
    component: ProfileComponent,
    data: {screenId: EnPageNameEnum.UserProfile, currentUserProfile: true}, runGuardsAndResolvers: 'always',
    canActivate: [UserAuthorizedCanActivate, NotProxyOnlyGuard]
  },
  {
    path: 'profile',
    component: ProfileComponent,
    data: {screenId: EnPageNameEnum.UserProfile, currentUserProfile: false}, runGuardsAndResolvers: 'always',
    canActivate: [UserAuthorizedCanActivate, NotProxyOnlyGuard]
  },
  {
    path: 'editprofile',
    component: EditProfileComponent,
    data: {screenId: EnPageNameEnum.EditProfile, Title: 'Edit Profile'}, runGuardsAndResolvers: 'always',
    canActivate: [UserAuthorizedCanActivate, NotProxyOnlyGuard]
  },
  {
    path: 'activate-user',
    component: ActivateUserPopupComponent,
    data: {screenId: EnPageNameEnum.ActivateUser, Title: 'Activate'}, runGuardsAndResolvers: 'always',
    canActivate: [UserAuthorizedCanActivate, NotProxyOnlyGuard]
  },
  {
    path: 'change-activation-state',
    component: DeactivateUserPopupComponent,
    data: {screenId: EnPageNameEnum.ChangeActivationState, Title: 'Deactivate'}, runGuardsAndResolvers: 'always',
    canActivate: [UserAuthorizedCanActivate, NotProxyOnlyGuard]
  },
  {
    path: 'link-account',
    component: LinkAccountModalComponent,
    data: {screenId: EnPageNameEnum.EditProfile, Title: 'Link Account'}, runGuardsAndResolvers: 'always',
    canActivate: [UserAuthorizedCanActivate, NotProxyOnlyGuard]
  },
  {
    path: 'add-new-login-popup',
    component: AddNewLoginModalComponent,
    data: {screenId: EnPageNameEnum.EditProfile, Title: 'Other Login'}, runGuardsAndResolvers: 'always',
    canActivate: [UserAuthorizedCanActivate, NotProxyOnlyGuard]
  },
  {
    path: 'clientsearch',
    component: ClientSearchComponent,
    data: {screenId: EnPageNameEnum.ClientSearch}, runGuardsAndResolvers: 'always',
    canActivate: [UserAuthorizedCanActivate, NotProxyOnlyGuard]
  },

  {
    path: 'set-security-question',
    component: SecurityQuestionComponent,
    data: {screenId: EnPageNameEnum.SecurityQuestion},
    canActivate: [UserAuthorizedCanActivate, NotProxyOnlyGuard]
  },
  {
    path: 'security-answers',
    component: SecurityAnswersComponent,
    data: {screenId: EnPageNameEnum.SecurityAnswers},
    canActivate: [NotProxyOnlyGuard]
  },
  {
    path: 'forgot-password',
    component: ForgotPasswordComponent,
    data: {screenId: EnPageNameEnum.ForgotPassword},
    runGuardsAndResolvers: 'always',
    canActivate: [UserUnAuthorizedCanActivate, NotProxyOnlyGuard]
  },
  {
    path: 'forgot-username',
    component: ForgotUsernameComponent,
    data: {screenId: EnPageNameEnum.ForgotUsername},
    runGuardsAndResolvers: 'always',
    canActivate: [UserUnAuthorizedCanActivate, NotProxyOnlyGuard]
  },
  {
    path: 'registration-investor',
    component: InvestorRegistrationComponent,
    data: {screenId: EnPageNameEnum.NewRegistrationInvestor},
    runGuardsAndResolvers: 'always',
    canActivate: [UserUnAuthorizedCanActivate, NotProxyOnlyGuard]
  },
  {
    path: 'registration-rep',
    component: RepRegistrationComponent,
    data: {screenId: EnPageNameEnum.NewRegistrationRep},
    runGuardsAndResolvers: 'always',
    canActivate: [UserUnAuthorizedCanActivate, NotProxyOnlyGuard]
  },
  {
    path: 'reset-password',
    component: ResetPasswordComponent,
    data: {screenId: EnPageNameEnum.ResetPassword},
    runGuardsAndResolvers: 'always',
    canActivate: [UserUnAuthorizedCanActivate, NotProxyOnlyGuard]
  },
  {
    path: '**',
    component: InvestorHomeComponent,
    data: {screenId: EnPageNameEnum.Home},
    canActivate: [UserAuthorizedCanActivate]}
];


@NgModule({ declarations: [
        AppComponent,
        GenericHomePageComponent,
        ForgotUsernameComponent,
        InvestorHomeComponent,
        RepHomeComponent,
        BrokerHomeComponent,
        CustodianHomeComponent,
        UserHomeComponent,
        OtherUserHomeComponent,
        LoginPageComponent,
        Login2FaComponent,
        FormsComponent,
        ReportsComponent,
        TopMenuComponent,
        LeftMenuComponent,
        LookingForHelpComponent,
        TaxComponent,
        StatementComponent,
        ClientStatementComponent,
        InvestmentsComponent,
        InvestmentDetailComponent,
        InboxComponent,
        SecurityQuestionComponent,
        SecurityAnswersComponent,
        ForgotPasswordComponent,
        ChangePasswordComponent,
        ResetPasswordComponent,
        InvestorRegistrationComponent,
        UpdateAddressComponent,
        EditProfileComponent,
        FAQComponent,
        ClientSearchComponent,
        ProfileComponent,
        PopUpDialogComponent,
        FormatLabelComponent,
        StarMaskedTextBoxComponent,
        DateChooserComponent,
        CheckboxComponent,
        ModalContainerDirective,
        DynamicModalComponent,
        ContactsComponent,
        FormsBlockComponent,
        AnnouncementBlockComponent,
        TableTitleComponent,
        DocumentsBlockComponent,
        PortfolioBlockComponent,
        DictionaryPipe,
        InvestmentsBlockComponent,
        StepperComponent,
        SecureMaskComponent,
        PopUpConfirmationComponent,
        RepRegistrationComponent,
        LinkAccountModalComponent,
        AddNewLoginModalComponent,
        EditSecurityQuestionsComponent,
        DeactivateUserPopupComponent,
        ActivateUserPopupComponent,
        ProxyComponent
    ],
    bootstrap: [AppComponent], imports: [BrowserModule,
        BrowserAnimationsModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatFormFieldModule,
        ReactiveFormsModule,
        MatInputModule,
        InputsModule,
        LabelModule,
        RouterModule.forRoot(parentroutes, {}),
        FormsModule,
        NgxUiLoaderModule.forRoot(ngxUiLoaderConfig),
        ButtonsModule,
        GridModule,
        DialogModule,
        TableModule,
        TooltipModule,
        AuthModule.forRoot({
            domain: environment.domain,
            clientId: environment.clientId,
            authorizationParams: {
                redirect_uri: redirectUri,
                audience: environment.audience,
                scope: environment.scope
            },
            //vistra auth0 tenant
            /*ng serve domain: 'dev-auth-fund.vistra.com',
             clientId: 'lLnRzUfpkSySk9gcBrVmhjLMBDDiz3g2',
             authorizationParams: {
               redirect_uri: window.location.origin, //'https://localhost:4200',
               audience:'https://api.star.com',
                scope: 'openid profile email'
             },
             /*marc custom auth0 tenant
             //domain: 'dev-cpik77a0o7my1zwh.us.auth0.com',   // e.g., 'dev-xyz.auth0.com'
             clientId: 'Y2E0unekNTFdWjL8Gcx2KJtjXLmd9UdC',  // e.g., 'abcd1234efgh5678ijkl9012'
              authorizationParams: {
               redirect_uri: window.location.origin, //'https://localhost:4200',
               audience:'https://star.api/developer.com',
                scope: 'openid profile email'
             },
             end marc auth0 tenant */
        })], providers: [
        AbstractDataProvider,
        LoginProvider,
        JqueryDialogService,
        AuthServiceCustom,
        UserAuthorizedCanActivate,
        UserUnAuthorizedCanActivate,
        DynamicModalService,
        DictionaryService,
        NotProxyOnlyGuard,
        UiComponentService,
        { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
        { provide: LocationStrategy, useClass: HashLocationStrategy },
        provideHttpClient(withInterceptorsFromDi()),
        providePrimeNG({
          theme: {
            preset: Aura,
            options: {
              darkModeSelector: false
            }
          }
        }),
    ] })
export class AppModule {
}
